// @flow
/* eslint-disable no-underscore-dangle */
import {camelCase, upperFirst} from 'lodash';
import {fromJS, Map} from 'immutable';

import {Store} from 'client/framework';
import appStore from 'client/AppStore';
import sessionStore from 'client/Session/SessionStore';
import {history} from 'client/history';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder/legacy';

import supplementManagerActions from 'client/Supplements/SupplementManager/SupplementManager.actions';
import {
  getQueueQuestionEditPath,
  getQuestionSetParams,
  getQuestionSetsQuery
} from 'client/Dennis/Content/Queue/shared';
import {AuthoringQuestionModelV1} from 'resources/augmented/AuthoringQuestion/AuthoringQuestionModel.v1';
import {AuthoringQuestionSetModelV1} from 'resources/GeneratedModels/AuthoringQuestionSet/AuthoringQuestionSetModel.v1';
import {AuthoringSupplementModelV1} from 'resources/GeneratedModels/AuthoringSupplement/AuthoringSupplementModel.v1';
import {Tag} from 'resources/GeneratedModels/Tag/TagModel.v1';
import constants from 'client/constants';
import makePayloadOmitter from 'lib/makePayloadOmitter';

import questionEditorActions from './QuestionEditor.actions';

export class QuestionEditorStore extends Store {
  static isomorphism = {
    dataTransforms: {
      question: (question: ?Map<string, *>) =>
        Map.isMap(question) ? new AuthoringQuestionModelV1(question) : question,
      questionSet: (questionSet: ?Map<string, *>) =>
        Map.isMap(questionSet) ? new AuthoringQuestionSetModelV1(questionSet) : questionSet
    }
  };

  constructor(name: string) {
    super(name);
    this.setInitialData(
      fromJS({
        pendingNewQuestions: [],
        question: null,
        questionSet: null,
        toastData: null,
        isReady: false,
        savePending: false,
        cursorPosition: {},
        newQuestionType: '',
        subjectId: '',
        hasChanges: false,
        validationErrors: {},
        isPreviewMode: false
      })
    );
    this.handle(questionEditorActions.CLEAR_TOAST_DATA, this._clearToastData);
    this.handle(questionEditorActions.INITIALIZE_STORE, this._initialize);
    this.handle(questionEditorActions.SAVE_QUESTION, this._saveQuestion);
    this.handle(questionEditorActions.SAVE_CURSOR_POSITION, this._saveCursorPosition);
    this.handle(questionEditorActions.SET_IS_PREVIEW_MODE, this.setProperty('isPreviewMode'));
    this.handle(questionEditorActions.SET_SUBJECT_ID, this._setSubjectId);
    this.handle(questionEditorActions.SUBMIT_FOR_REVIEW, this._submitForReview);
    this.handle(questionEditorActions.REVERT_TO_DRAFT, this._revertToDraft);
    this.handle(questionEditorActions.APPROVE_QUESTION_SET, this._approveQuestionSet);
    this.handle(questionEditorActions.REASSIGN_QUESTION_SET, this._reassignQuestionSet);
    this.handle(questionEditorActions.REJECT_QUESTION_SET, this._rejectQuestionSet);
    this.handle(questionEditorActions.UPDATE_QUESTION, this._updateQuestion);
    this.handle(questionEditorActions.UPDATE_QUESTION_DETAILS, this._updateQuestionDetails);
    this.handle(questionEditorActions.UPDATE_QUESTION_SET_STATUS, this._updateQuestionSetStatus);
    this.handle(questionEditorActions.UPDATE_QUESTION_SET_TAGS, this._updateQuestionSetTags);
    this.handle(questionEditorActions.HIDDEN_QUESTION_SET, this._hiddenQuestionSet);
    this.handle(questionEditorActions.ARCHIVED_QUESTION_SET, this._archivedQuestionSet);
    this.handle(questionEditorActions.UPDATE_QUESTION_STANDARDS, this._updateQuestionStandards);
    this.handle(questionEditorActions.VALIDATE_QUESTION_FIELD, this._validateQuestionField);
    this.handle(questionEditorActions.RESET_STORE, this.resetStore);
    this.handle(
      supplementManagerActions.INSERT_SUPPLEMENT_AT_CURSOR_POSITION,
      this._insertSupplementAtCursorPosition
    );
  }

  async _initialize(type: string): Promise<*> {
    this.writeData('isReady', false);

    // This will ensure that going from a new question to a new question will trigger the update logic
    // that question editors may need in order to re-initialize themselves
    await new Promise((res) => setTimeout(res, 0));

    let question;
    let questionSet;

    const {questionId, questionSetId} = getQuestionSetParams();
    try {
      question = questionId
        ? await this.getQuestionQuery().getResourcePromise()
        : AuthoringQuestionModelV1.getDefaultModel();
      questionSet = questionSetId
        ? await this.getQuestionSetQuery().getResourcePromise()
        : AuthoringQuestionSetModelV1.getDefaultModel();
    } catch (e) {
      this._setToastData('danger', 'There was a problem fetching the question.');
    }

    if (type) {
      this._setNewQuestionType(type);
    }

    if (questionSetId && !questionId && !this.getQuestionType()) {
      /* eslint-disable-next-line no-shadow */
      const questionId = questionSet.getAuthoringQuestions().first().getId();
      history.pushState(null, `${appStore.routerProps.location.pathname}/${questionId}`);
    }

    if (!questionId) {
      question = question.setDefaultsByQuestionType(this.getQuestionType());
    }

    if (!questionSetId) {
      questionSet = questionSet
        .setStatus('draft')
        .setSubjectId(this.getSubjectId())
        .addRelationship({
          type: 'authoring_subject_v1',
          relation: this.getSubjectId()
        })
        .addRelationship({
          type: 'author_v1',
          relation: sessionStore.getUserId()
        })
        .addRelationship({
          type: 'owner_v2',
          relation: sessionStore.getUserId()
        });
    }
    this.writeData('question', question);
    this.writeData('questionSet', questionSet);
    this.writeData('validationErrors', Map());
    this.writeData('isReady', true);
    this.writeData('hasChanges', false);
  }

  _insertSupplementAtCursorPosition({
    fieldPath,
    supplement
  }: {
    fieldPath: string | Array<string>,
    supplement: AuthoringSupplementModelV1
  }) {
    /* eslint-disable-next-line no-param-reassign */
    fieldPath = Array.isArray(fieldPath) ? fieldPath : fieldPath.split(',');
    const cursorPosition = this.getCursorPosition(fieldPath);
    const start = cursorPosition.get('start');
    const end = cursorPosition.get('end');
    const escapedName = supplement.getName().replace(/[:]/g, '-');
    const supplementShortcode = `[s:${supplement.getId()}:${escapedName}:${supplement.getType()}]`;
    const stringToModify = this.getQuestion().getIn(fieldPath, '');
    let modifiedString;
    if (start && end) {
      modifiedString =
        stringToModify.substr(0, start) + supplementShortcode + stringToModify.substr(end);
    } else {
      modifiedString = stringToModify + supplementShortcode;
    }
    const updatedQuestion = this.getQuestion().setFieldIn(fieldPath, modifiedString);
    this.writeData('question', updatedQuestion);
    this._setHasChanges();
  }

  _saveCursorPosition({start, end, name}: {start: number, end: number, name: string}) {
    const updatedCursorPosition = this.getCursorPosition()
      .setIn(name.split(',').concat('start'), start)
      .setIn(name.split(',').concat('end'), end);
    this.writeDataNoEmit('cursorPosition', updatedCursorPosition);
  }

  _saveQuestion = async (skipSaveToast = false): Promise<*> => {
    const question = this.getQuestion();
    const questionSet = this.getQuestionSet();

    if (this.hasErrors()) {
      this._validateQuestion();
      this._setToastData('danger', 'The question could not be saved due to validation errors.');
      return;
    }

    // Mandark needs the payload to be validResponse instead of valid_response ¯\_(ツ)_/¯
    const rubricValidResponse = question.getRubric().get('valid_response', Map());
    const updatedRubric = question
      .getRubric()
      .set('validResponse', rubricValidResponse)
      .remove('valid_response');
    const formattedQuestion = question.setRubric(updatedRubric);
    this.writeData('savePending', true);
    try {
      const savedQuestionSet = await questionSet
        .replaceRelationship({
          type: 'tags_v1',
          relation: questionSet.getTags().map((tag) => tag.getId())
        })
        .save({
          customQuery: this._getSaveQuestionSetCustomQuery(),
          payloadMutator: makePayloadOmitter({
            reorder_questions: (val: ?Array<any>) => !val || val.length === 0,
            slug_id: (val: ?string) => !val
          })
        });
      const savedQuestion = await formattedQuestion
        .replaceRelationship({
          type: 'standards_v1',
          relation: question.getStandards().map((standard) => standard.getId())
        })
        .addRelationship({
          type: 'authoring_question_set_v1',
          relation: savedQuestionSet.getId()
        })
        .save({
          customQuery: this._getSaveQuestionCustomQuery(),
          payloadMutator: makePayloadOmitter({
            published: () => true
          })
        });
      this._clearHasChanges();
      if (!skipSaveToast) {
        this._setToastData('success', 'The question was successfully saved.');
      }
      this.writeData('questionSet', savedQuestionSet);
      this.writeData('question', savedQuestion);
      if (!question.getId()) {
        history.pushState(
          null,
          getQueueQuestionEditPath(savedQuestionSet.getId(), savedQuestion.getId())
        );
      }
    } catch (err) {
      logger.error('There was an error saving a question: ', err.detail || err);
      this._setToastData('danger', 'There was a problem saving the question.');
    } finally {
      this.writeData('savePending', false);
      invalidatePartialInterest({
        resourcePath: getQuestionSetsQuery().done().resourcePath
      });
    }
  };

  async _saveTag(tagModel: Tag): Promise<*> {
    /* eslint-disable-next-line @typescript-eslint/return-await */
    return await tagModel.save();
  }

  _submitForReview() {
    this._saveNewQuestionSetStatus({
      status: constants.QUESTIONSET_STATUSES.IN_REVIEW
    });
  }

  _revertToDraft() {
    this._saveNewQuestionSetStatus({
      status: constants.QUESTIONSET_STATUSES.DRAFT_MODE
    });
  }

  _approveQuestionSet() {
    this._saveNewQuestionSetStatus({
      status: constants.QUESTIONSET_STATUSES.PUBLISHED
    });
  }

  _rejectQuestionSet() {
    this._saveNewQuestionSetStatus({
      status: constants.QUESTIONSET_STATUSES.REJECTED
    });
  }

  _hiddenQuestionSet() {
    this._saveNewQuestionSetStatus({
      status: constants.QUESTIONSET_STATUSES.HIDDEN
    });
  }

  _archivedQuestionSet() {
    this._saveNewQuestionSetStatus({
      status: constants.QUESTIONSET_STATUSES.ARCHIVED
    });
  }

  async _saveNewQuestionSetStatus({status}: {status: string}): Promise<*> {
    const successMessage = 'Status updated and changes saved.';
    await this._saveQuestion({skipSaveToast: true});

    this.writeData('savePending', true);
    try {
      const updatedQuestionSet = await this.getQuestionSet().setStatus(status).save({
        customQuery: this._getSaveQuestionSetCustomQuery()
      });
      this.writeData('questionSet', updatedQuestionSet);
      this._setToastData('success', successMessage);
      invalidatePartialInterest({
        resourcePath: getQuestionSetsQuery().done().resourcePath
      });
    } catch (err) {
      logger.error('There was an error updating question set status: ', err.message || err);
      this._setToastData('danger', 'We encountered a problem. Please try again.');
    } finally {
      this.writeData('savePending', false);
    }
  }

  async _reassignQuestionSet(newAuthorId: string): Promise<any> {
    this.writeData('savePending', true);
    try {
      if (newAuthorId && this.getQuestionSet().getMeta().getAuthorId() !== newAuthorId) {
        const updatedQuestionSet = await this.getQuestionSet()
          .replaceRelationship({
            type: 'author_v1',
            relation: newAuthorId
          })
          .replaceRelationship({
            type: 'owner_v1',
            relation: newAuthorId
          })
          .save({
            customQuery: this._getSaveQuestionSetCustomQuery()
          });
        invalidatePartialInterest({
          resourcePath: getQuestionSetsQuery().done().resourcePath
        });
        this.writeData('questionSet', updatedQuestionSet);
        this._setToastData('success', 'Successfully reassigned question set!');
      }
    } catch (err) {
      logger.error("There was an error reassining a question set's author: ", err.message || err);
      this._setToastData('error', 'We encountered a problem. Please try again.');
    } finally {
      this.writeData('savePending', false);
    }
  }

  _clearToastData() {
    this.writeData('toastData', null);
  }

  _setToastData(type, text) {
    this.writeData('toastData', Map({type, text}));
  }

  _setSubjectId(subjectId: string) {
    this.writeData('subjectId', subjectId);
  }

  _setNewQuestionType(type: string) {
    this.writeData('newQuestionType', type);
  }

  _validateQuestion = () => {
    const validationErrors = Object.entries(this.getQuestion().validators).reduce(
      (acc, [key, func]) => {
        const errors = func();
        if (errors) {
          const fieldName = key.replace(/get\w/, (match) => match[match.length - 1].toLowerCase());
          return acc.set(fieldName, errors.first());
        }
        return acc;
      },
      Map()
    );
    this.writeData('validationErrors', validationErrors);
  };

  _validateQuestionField(validateMethod) {
    const functionToValidate = `get${upperFirst(camelCase(validateMethod))}`;
    const error = this.getQuestion().validators[functionToValidate]();
    const errorMessage = error ? error.first() : null;
    const validationErrors = this.getValidationErrors().set(validateMethod, errorMessage);
    this.writeData('validationErrors', validationErrors);
  }

  _updateQuestion(question) {
    this.writeData('question', question);
    this._setHasChanges();
  }

  _updateQuestionDetails({setter, value}: {setter: string, value: string}) {
    const updatedQuestion = this.getQuestion()[setter](value);
    this.writeData('question', updatedQuestion);

    const questionSet = this.getQuestionSet();
    const countOfQuestions = questionSet.getMeta().getCountOfQuestions() || 0;

    if (setter === 'setTitle' && countOfQuestions <= 1) {
      const updatedQuestionSet = questionSet.setName(value);
      this.writeData('questionSet', updatedQuestionSet);
    }

    this._setHasChanges();
  }

  _updateQuestionSetStatus({status}: {status: string}) {
    const updatedQuestionSet = this.getQuestionSet().setStatus(status);
    this.writeData('questionSet', updatedQuestionSet);
    this._setHasChanges();
  }

  _updateQuestionSetTags({tag, remove}) {
    const questionSet = this.getQuestionSet();
    let tags = questionSet.getTags();
    const hasTag = tags.find((existingTag) => {
      return existingTag.getName() === tag.getName();
    });
    if (!remove && hasTag) {
      return;
    }
    if (remove) {
      const tagIndex = tags.indexOf(tag);
      tags = tags.delete(tagIndex);
    } else if (tag.existsOnServer() === true) {
      tags = tags.push(tag);
    }
    const updatedQuestionSet = questionSet.setField('tags', tags);
    this.writeData('questionSet', updatedQuestionSet);
    this._setHasChanges();
  }

  _updateQuestionStandards(standards) {
    const question = this.getQuestion();
    const updatedQuestion = question.setField('standards', standards);
    this.writeData('question', updatedQuestion);
    this._setHasChanges();
  }

  _clearHasChanges() {
    this.writeData('hasChanges', false);
  }

  _setHasChanges() {
    if (!this.hasChanges()) {
      this.writeData('hasChanges', true);
    }
  }

  /**
   * Since we repopulate the store with the saved question, we have
   * to make sure the response has all the proper includes and metadata.
   *
   * @example
   * .save({ customQuery: this._getSaveQuestionCustomQuery() })
   */
  _getSaveQuestionCustomQuery(): Object {
    return {
      include: this.getQuestionQuery().done().include
    };
  }

  /**
   * Since we repopulate the store with the saved question set, we have
   * to make sure the response has all the proper includes and metadata.
   *
   * @example
   * .save({ customQuery: this._getSaveQuestionSetCustomQuery() })
   */
  _getSaveQuestionSetCustomQuery(): Object {
    const builtQuestionSetQuery = this.getQuestionSetQuery().done();
    return {
      include: builtQuestionSetQuery.include,
      customQuery: builtQuestionSetQuery.customQuery
    };
  }

  getQuestionQuery(): QueryBuilder {
    const {questionId} = appStore.routerProps.params;
    return resource('authoring_question_v1')
      .mandarkEndpoint(['authoring_questions_v1', questionId])
      .withMeta('authoring_question_v1')
      .include('authoring_question_set_v1.authoring_guide_levels_v1,authoring_supplements_v1')
      .include('authoring_question_set_v1.tags_v1')
      .include('authoring_question_set_v1.author_v1', sessionStore.isSuper())
      .include('standards_v1.standard_set_v1');
  }

  getQuestionSetQuery(): QueryBuilder {
    const {questionSetId} = appStore.routerProps.params;
    return resource('authoring_question_set_v1')
      .mandarkEndpoint(['authoring_question_sets_v1', questionSetId])
      .include('tags_v1')
      .include('authoring_guide_levels_v1')
      .include('authoring_exams_v1')
      .include('sections_v1')
      .include('authoring_subject_v1')
      .include('authoring_questions_v1')
      .withMeta('authoring_question_set_v1');
  }

  getQuestion(): AuthoringQuestionModelV1 {
    return this.readData('question');
  }

  getQuestionSet(): AuthoringQuestionSetModelV1 {
    return this.readData('questionSet');
  }

  getQuestionType(): string {
    return this.readData('newQuestionType');
  }

  getSubjectId(): string {
    return this.readData('subjectId');
  }

  getCursorPosition(fieldPath: Array<*> = []): Map<string, *> {
    return this.readData('cursorPosition').getIn(fieldPath, Map());
  }

  getValidationErrors(): Map<string, *> {
    return this.readData('validationErrors');
  }

  getToastType(): string {
    return this.readData('toastData').get('type');
  }

  getToastText(): string {
    return this.readData('toastData').get('text');
  }

  hasErrors(): boolean {
    return this.getQuestion().validators.hasValidationErrors();
  }

  hasChanges(): boolean {
    return this.readData('hasChanges');
  }

  hasToastData(): boolean {
    return this.readData('toastData') !== null;
  }

  isReady(): boolean {
    return this.readData('isReady');
  }

  isPreviewMode(): boolean {
    return this.readData('isPreviewMode');
  }

  isSavePending(): boolean {
    return this.readData('savePending');
  }
}

export default new QuestionEditorStore('QuestionEditorStore');
